import React, { useMemo } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classNames from 'classnames';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import { ListLayout } from '../../../global/tables/table-list-layout';
import DefaultButton from '../../../global/default-button/default-button';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import { ListDayItem } from '../shared/list-day-item/list-day-item';
import { Paging } from '../../../global/_paging/paging-container';
import { IMeetingDetailsDTO } from '../../../../api/meetings.dao';
import OptionsButton from '../../../../components/global/options-button/options-button';
import { UploadMeetingsModal } from './_upload-meetings-button/_upload-meetings-modal/upload-meetings-modal.presentation';
import { groupListByTime, isPreviousGroupDayDifferent } from '../../../../infrastructure/utils/common.utils';
import SingleSelectionDropdown from '../../../global/single-selection-dropdown/single-selection-dropdown';
import { SearchInput } from '../../../global/search-input/search-input';
import './meeting-list.scss';
import { IAttendeeDetailsDTO, IAttendeeOrganizationDTO } from '../../../../api/attendees.dao';
import { AddNewEntityButton } from '../../../global/add-new-entity/add-new-entity-button';
import { Permission } from '../../../../api/roles.dao';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { SubWindowStatus } from '../../../../infrastructure/sub-windows';
import DateMultiSelectionDropdown from '../../../global/date-multi-selection-dropdown/date-multi-selection-dropdown';
import MultiSelectionDropdown from '../../../global/multi-selection-dropdown/multi-selection-dropdown';
import { getSortedMeetingsByPresenterName } from '../../../../infrastructure/utils/sorting.utils';
import { BulkActionModal } from './_bulk-action-modal/bulk-action-modal.presentation';
import { MeetingsListViewModel } from './meetings-list.viewmodel';
import { MeetingListItem } from './_meeting-list-item/meeting-list-item';
import { OpenFrom } from '../../../../tracking-service/tracking-values';
import { EMPTY_SEARCH_RESULT, MORE_BUTTON_TEXT } from '../../../../constants/common-labels.constants';
import { AssignRoomModal } from './_assign-room/assign-room-modal.presentation';
import { MeetingCoPilotModal } from './_meeting-co-pilot/_meeting-co-pilot-modal/meeting-co-pilot-modal.presentation';
import { meetingMatchesSearch } from '../../../../infrastructure/utils/meetings-filtering.utils';
import { MINIMAL_SEARCH_LENGTH } from '../../../../constants/common.constants';
import { EDIT_AGENT_MEMORY_MENU_ITEM_TEXT } from '../_control/_agent-memory-form/agent-memory-form.text';

interface IProps {
    vm: MeetingsListViewModel;
    intl: IntlShape;
}

const MeetingsListPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        vm,
        intl,
    } = props;
    const { withoutRoom } = vm;

    const noMeetingTypesFilterPlaceholder = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.meeting.noTypesFound'
                    defaultMessage="You don't have any meeting with type yet."
                    description='No meeting types label text'
                />
            </div>
        </div>
    );

    const thead = [
        <DateMultiSelectionDropdown
            eventTimezone={vm.getEvent().timeZone}
            fetchHours={vm.fetchFilterHours}
            filterHoursList={vm.filterHours}
            selectedHours={vm.selectedFilterHours}
            selectedSortOrder={vm.selectedSortOrder}
            onApply={vm.applyFilterHours}
        />,
        intl.formatMessage({
            id: 'oe.common.presenters',
            defaultMessage: 'Presenters',
            description: 'Label text',
        }),
        intl.formatMessage({
            id: 'oe.common.participants',
            defaultMessage: 'Participants',
            description: 'Label text',
        }),
        intl.formatMessage({
            id: 'oe.common.hosts',
            defaultMessage: 'Hosts',
            description: 'Label text',
        }),
        <MultiSelectionDropdown
            entityName={intl.formatMessage({
                id: 'oe.common.meetingType',
                defaultMessage: 'Meeting Type',
                description: 'Label text'
            })}
            noItemsPlaceholder={noMeetingTypesFilterPlaceholder}
            onApply={types => vm.handleChangeTypeFilter(types)}
            onFetchItems={vm.fetchMeetingTypes}
            savedItems={!!vm.selectedFilterTypes ? vm.selectedFilterTypes.split(',') : []}
            isSetInitialItems={true}
        />,
        intl.formatMessage({
            id: 'oe.common.meetingTopic',
            defaultMessage: 'Meeting Topic',
            description: 'Label text',
        }),
        <SingleSelectionDropdown
            entityName={intl.formatMessage({
                id: 'oe.common.rooms',
                defaultMessage: 'Rooms',
                description: 'Label text',
            })}
            withoutRoom={withoutRoom}
            onApply={param => {
                vm.setWithoutRoom(param);
                vm.fetchMeetingsRequest();
            }}
            accountId={vm.accountId}
            eventId={vm.getEvent().eventId}
        />,
        <div className={classNames('search-input-container', {
            'no-result': (
                !vm.getMeetingsList()?.length &&
                !!vm.searchQuery
            )
        })}>
            <SearchInput
                onChange={(e, value) => {
                    vm.setSearchQuery(value);
                }}
                withResetSearchButton
                withDebounce
                debounceTime={500}
            />
        </div>
    ];

    const isLoading = vm.isLoading();
    const meetingsList = vm.getMeetingsList();

    const filteredMeetingsList = useMemo(() => {
        if (vm.searchQuery.trim().length >= MINIMAL_SEARCH_LENGTH) {
            return meetingsList?.filter(meeting => meetingMatchesSearch(meeting, vm.searchQuery));
        } else {
            return meetingsList;
        }
    },
        [meetingsList, vm.searchQuery]
    );

    const meetingsTimeGroups = groupListByTime(filteredMeetingsList);
    const event = vm.getEvent();

    const assignRoomsButtonProperties = vm.composeAssignRoomsButtonProperties();

    const uploadMeetingsButtonProperties = vm.composeUploadMeetingButtonProperties();

    const meetingsOptions = [
        hasPermission(Permission.SCHEDULE_UPLOAD) && {
            label: uploadMeetingsButtonProperties.text,
            disabled: uploadMeetingsButtonProperties.disabled,
            action: vm.showForm,
        },
        hasPermission(Permission.SCHEDULE_DOWNLOAD) && {
            label: intl.formatMessage({
                id: 'oe.meetings.download',
                defaultMessage: 'Download Schedule',
                description: 'Label text',
            }),
            action: vm.downloadMeetingsCSV
        },
        hasPermission(Permission.GENERATE_REGISTRANTS_BY_SESSION) && {
            label: intl.formatMessage({
                id: 'oe.meetings.downloadRegistrantsBySession',
                defaultMessage: 'Download Registrants by Session',
                description: 'Label text',
            }),
            action: vm.openRegistrantsBySessionReportModal
        },
        hasPermission(Permission.EDIT_EVENT) && {
            label: intl.formatMessage({
                id: 'oe.meetings.editGuidelines',
                defaultMessage: 'Edit Guidelines',
                description: 'Edit general guidelines'
            }),
            action: () => {
                if (!vm.guidelinesFormSubWindow.create || vm.guidelinesFormSubWindow.create.getStatus() === SubWindowStatus.Closed) {
                    vm.showEventNotesPopup(true, OpenFrom.Schedule);
                }
            }
        },
        hasPermission(Permission.MANAGE_AGENT_MEMORY) && {
            label: intl.formatMessage(EDIT_AGENT_MEMORY_MENU_ITEM_TEXT),
            action: () => vm.showAgentMemoryPopup(OpenFrom.Schedule)
        },
        hasPermission(Permission.ASSIGN_ROOMS) && {
            id: 'assignRooms',
            label: assignRoomsButtonProperties.text,
            action: vm.showAssignRoomsForm,
            disabled: assignRoomsButtonProperties.disabled,
        },
        hasPermission(Permission.CREATE_MEETING_WITH_AI) && {
            id: 'meetingCoPilot',
            label: intl.formatMessage({
                id: 'oe.meetings.meetingCoPilot',
                defaultMessage: 'Meeting Co-Pilot',
                description: 'Create Meeting with Co-Pilot'
            }),
            action: vm.showMeetingCoPilotForm,
        },
        hasPermission(Permission.BULK_MEETING_STATUS_UPDATE) && {
            label: intl.formatMessage({
                id: 'oe.meetings.bulkCancel',
                defaultMessage: 'Bulk Cancel Meetings',
                description: 'Label text',
            }),
            action: vm.showBulkCancelForm,
            disabled: vm.isMeetingsListEmpty() || !vm.isRuntimeReady(),
            danger: true
        },
        hasPermission(Permission.BULK_MEETING_STATUS_UPDATE) && {
            label: intl.formatMessage({
                id: 'oe.meetings.bulkRemove',
                defaultMessage: 'Bulk Remove Meetings',
                description: 'Label text',
            }),
            action: vm.showBulkRemoveForm,
            disabled: vm.isMeetingsListEmpty() || !vm.isRuntimeReady(),
            danger: true
        },
    ].filter(Boolean);

    const isEmpty = !filteredMeetingsList?.length && !isLoading;

    return (
        <div className='event-page-content'>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {
                            hasPermission(Permission.CREATE_NEW_MEETING) &&
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={intl.formatMessage({
                                    id: 'oe.meetings.newMeeting',
                                    defaultMessage: '{prefix}New Meeting',
                                    description: 'Label text',
                                },
                                    {
                                        prefix: '+ '
                                    })}
                                onClick={vm.handleCreateClick}
                            />
                        }
                        {!!meetingsOptions.length && <OptionsButton
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={meetingsOptions}
                        />}
                    </div>
                </div>
            </section>
            <section className='fixed-list-layout'>
                <div>
                    <ListLayout
                        thead={thead}
                        isDisabled={isEmpty}
                    >
                        {isLoading && <TableLoadingRow colSpan={thead.length} />}
                        {!isLoading && meetingsTimeGroups.map((group: IMeetingDetailsDTO[], groupIndex) => {
                            const prevGroup = meetingsTimeGroups.length && meetingsTimeGroups[groupIndex - 1];
                            const isDayRendered = prevGroup ? isPreviousGroupDayDifferent(group, prevGroup, event.timeZone) : false;

                            return group.length && (
                                <React.Fragment key={group[0].meetingId}>
                                    {!isDayRendered &&
                                        <ListDayItem
                                            date={group[0].startDateTime}
                                            eventTimezone={event.timeZone}
                                            colSpan={thead.length}
                                            className='column-with-filters'
                                        />}
                                    {getSortedMeetingsByPresenterName<IMeetingDetailsDTO>(group).map((meeting: IMeetingDetailsDTO, meetingIndex) => (
                                            <MeetingListItem
                                                {...meeting}
                                                handleEditClick={vm.handleEditClick}
                                                key={meeting.meetingId}
                                                eventTimezone={event.timeZone}
                                                isSelected={vm.isMeetingEditFormShown(meeting.meetingId)}
                                                isFirstItemInDayGroup={!isDayRendered && meetingIndex === 0}
                                                isStartEndTimeRenderedInPreviousRow={meetingIndex !== 0}
                                                searchQuery={vm.searchQuery}
                                                accountId={vm.accountId}
                                                eventId={event.eventId}
                                                handleRemoveAttendeeClick={(attendeeId: number, organizationId: number, key: string) => {
                                                    vm.handleRemoveAttendeeClick(meeting, attendeeId, organizationId, key);
                                                }}
                                                handleAttendeesChange={(attendees: IAttendeeOrganizationDTO[], key: string) => {
                                                    vm.updateMeetingAttendeesFromMeetingList(meeting, attendees, key); }
                                                }
                                                createAttendeeFromForm={key => <AddNewEntityButton
                                                    permission={Permission.CREATE_ATTENDEE}
                                                    onClick={() => {
                                                        vm.createAttendee((attendee: IAttendeeDetailsDTO) => vm.addNewAttendeeByRole(meeting, attendee, key)); }
                                                    }
                                                    label={
                                                        <FormattedMessage
                                                            description='Label text'
                                                            defaultMessage='{prefix}New Attendee'
                                                            id='oe.attendee.new'
                                                            values={{
                                                                prefix: '+ '
                                                            }}
                                                        />
                                                    }
                                                />}
                                                attendees={vm.attendees}
                                                fetchAttendeeList={async () => await vm.fetchAttendeeListHandler(meeting.meetingId)}
                                                isEditable={hasPermission(Permission.UPDATE_MEETING)}
                                                loadingAttendees={vm.loadingAttendees}
                                                resetLoadingState={vm.resetLoadingState}
                                                attendeeDetailsPopoverVM={vm.attendeDetailsVM}
                                                generateMeetingInteractionsReport={vm.generateMeetingInteractionsReport}
                                                generateMeetingRegistrationReport={vm.generateMeetingRegistrationReport}
                                            />
                                        ))}
                                </React.Fragment>
                            );
                        })}
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            {!vm.searchQuery ?
                                <FormattedMessage
                                    description='Meeting list empty text'
                                    defaultMessage='There are no meetings scheduled in this event.'
                                    id='oe.meetings.emptyListText'
                                />
                                :
                                <FormattedMessage {...EMPTY_SEARCH_RESULT} />
                            }
                        </div>
                    }
                </div>
                {!isLoading && <Paging pagingVM={vm.getPaginationVM()}/>}
            </section>
            <UploadMeetingsModal vm={vm} />
            <AssignRoomModal vm={vm} />
            <MeetingCoPilotModal vm={vm.getMeetingCoPilotFormDataVM()} />
            {vm.getIsBulkRemoveFormShown() && (
                <BulkActionModal
                    onCancel={vm.hideBulkRemoveMeetingsForm}
                    onSubmit={vm.triggerBulkRemoveMeetings}
                    accountId={vm.accountId}
                    event={vm.getEvent()}
                    requiresConfirmation={true}
                    formLabelText={
                        <FormattedMessage
                            id='oe.common.datesRemove'
                            defaultMessage='Select dates to remove all scheduled meetings'
                            description='Label text'
                        />
                    }
                    modalTitle={
                        <FormattedMessage
                            description='Label text'
                            defaultMessage={'Bulk Remove Meetings'}
                            id={'oe.meetings.bulkRemove'}
                        />
                    }
                    selectedDatesLabelProps={
                        {
                            defaultMessage: 'Remove {prefix} Dates',
                            id: 'oe.meetings.removeDates'
                        }
                    }
                    alertMessage={
                        intl.formatMessage({
                            id: 'oe.alerts.removeAll',
                            defaultMessage: 'Are you sure you want to Remove all scheduled meetings on',
                            description: 'Alert message'
                        })
                    }
                    alertConfirmButtonText={
                        <FormattedMessage
                            description='Label text'
                            defaultMessage='Remove Meetings'
                            id='oe.meetings.remove'
                        />
                    }
                />
            )}
            {vm.getIsBulkCancelFormShown() && (
                    <BulkActionModal
                        excludeCanceledDates={true}
                        onCancel={vm.hideBulkCancelMeetingsForm}
                        onSubmit={vm.triggerBulkCancelMeetings}
                        accountId={vm.accountId}
                        event={vm.getEvent()}
                        formLabelText={
                            <FormattedMessage
                                id='oe.common.datesCancel'
                                defaultMessage='Select dates to cancel all scheduled meetings'
                                description='Label text'
                            />
                    }
                    modalTitle={
                        <FormattedMessage
                            description='Label text'
                            defaultMessage={'Bulk Cancel Meetings'}
                            id={'oe.meetings.bulkCancel'}
                        />
                    }
                    selectedDatesLabelProps={{
                        defaultMessage: 'Cancel {prefix} Dates',
                        id: 'oe.meetings.cancelDates'
                    }}
                    requiresConfirmation={true}
                    alertMessage={
                        intl.formatMessage({
                            id: 'oe.alerts.cancelAll',
                            defaultMessage: 'Are you sure you want to cancel all meetings on',
                            description: 'Alert message'
                        })
                    }
                    alertConfirmButtonText={
                        <FormattedMessage
                            description='Label text'
                            defaultMessage='Cancel Meetings'
                            id='oe.meetings.cancel'
                        />
                    }
                />
            )}
        </div>
    );
}));

const MeetingsListContainer = ({ vm }: { vm: MeetingsListViewModel }) => {
    React.useEffect(() => {
        vm.onMount();
        return vm.onUnmount;
    }, []);

    return <MeetingsListPresentation vm={vm} />;
};

export { MeetingsListContainer as MeetingsList, MeetingsListPresentation };
